import { useEffect, useState, type FormEvent } from 'react'
import { AlertCircle, CheckCircle2, ClipboardCopy, Globe2, HelpCircle, Plus, RefreshCw, Trash2, XCircle } from 'lucide-react'
import { api } from '../../api/client'
import type { DNSCheck, Domain } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

type DomainRecord = Domain

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }
  return (
    <button onClick={copy} className="shrink-0 rounded-md p-1.5 hover:bg-muted transition-colors" title="Copy">
      {copied ? <CheckCircle2 size={14} className="text-emerald-500" /> : <ClipboardCopy size={14} className="text-muted-foreground" />}
    </button>
  )
}

function DNSGuide({ domain, dnsChecks }: { domain: DomainRecord; dnsChecks: DNSCheck[] }) {
  const { t } = useI18n()

  const records = [
    { type: 'MX', check: dnsChecks.find(c => c.recordType === 'MX'), host: domain.name, description: t('admin.dnsMxDesc') },
    { type: 'SPF', check: dnsChecks.find(c => c.recordType === 'SPF'), host: domain.name, description: t('admin.dnsSpfDesc') },
    { type: 'DMARC', check: dnsChecks.find(c => c.recordType === 'DMARC'), host: `_dmarc.${domain.name}`, description: t('admin.dnsDmarcDesc') },
    { type: 'DKIM', check: dnsChecks.find(c => c.recordType === 'DKIM'), host: `${domain.dkimSelector}._domainkey.${domain.name}`, description: t('admin.dnsDkimDesc') },
  ]

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <HelpCircle size={16} className="text-primary" />
        <h3 className="text-sm font-semibold">{t('admin.dnsGuideTitle')}</h3>
      </div>
      {records.map(({ type, check, host, description }) => (
        <div key={type} className={`rounded-lg border p-4 transition-colors ${check?.valid ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/30'}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              {check?.valid ? <CheckCircle2 size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-amber-500" />}
              <span className="text-sm font-semibold">{type}</span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${check?.valid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {check?.valid ? t('admin.dnsOk') : t('admin.dnsPending')}
              </span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-2.5">{description}</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 rounded-md bg-background border px-3 py-2">
              <span className="text-[10px] font-medium text-muted-foreground uppercase w-10 shrink-0">{t('admin.dnsHost')}</span>
              <code className="text-xs flex-1 break-all">{host}</code>
              <CopyButton text={host} />
            </div>
            <div className="flex items-center gap-2 rounded-md bg-background border px-3 py-2">
              <span className="text-[10px] font-medium text-muted-foreground uppercase w-10 shrink-0">{t('admin.dnsValue')}</span>
              <code className="text-xs flex-1 break-all">{check?.expected || '—'}</code>
              <CopyButton text={check?.expected || ''} />
            </div>
            {check?.value && !check.valid && (
              <div className="flex items-start gap-2 rounded-md bg-background border border-amber-200 px-3 py-2">
                <AlertCircle size={12} className="text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <span className="text-[10px] font-medium text-amber-600">{t('admin.dnsCurrent')}</span>
                  <code className="text-xs break-all block mt-0.5">{check.value}</code>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export function DomainsPage() {
  const { t } = useI18n()
  const [items, setItems] = useState<DomainRecord[]>([])
  const [name, setName] = useState('')
  const [selector, setSelector] = useState('default')
  const [error, setError] = useState('')
  const [creating, setCreating] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = () => api.get<DomainRecord[]>('/admin/domains').then(setItems).catch((reason: Error) => setError(reason.message))
  useEffect(() => { void load() }, [])

  async function create(event: FormEvent) {
    event.preventDefault()
    try {
      setCreating(true)
      const result = await api.post<{ domain: Domain; dnsChecks: DNSCheck[] }>('/admin/domains', { name, dkimSelector: selector })
      const newDomain = { ...result.domain, dnsChecks: result.dnsChecks }
      setItems((current) => [newDomain, ...current])
      setName('')
      setShowForm(false)
      setExpanded(newDomain.id)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to create domain.')
    } finally {
      setCreating(false)
    }
  }

  async function check(item: DomainRecord) {
    try {
      const checks = await api.post<DNSCheck[]>(`/admin/domains/${item.id}/dns-check`)
      setItems((current) => current.map((value) => value.id === item.id ? { ...value, dnsChecks: checks } : value))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to check DNS.')
    }
  }

  async function remove(id: string) {
    if (!confirm(t('admin.domainDeleteConfirm'))) return
    try {
      await api.delete(`/admin/domains/${id}`)
      setItems((current) => current.filter((item) => item.id !== id))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to delete domain.')
    }
  }

  const allValid = (checks?: DNSCheck[]) => checks && checks.length > 0 && checks.every(c => c.valid)
  const validCount = (checks?: DNSCheck[]) => checks ? checks.filter(c => c.valid).length : 0

  return <>
    <PageHeader title="admin.domains" action={
      <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
        <Plus size={16} />{t('admin.addDomain')}
      </button>
    } />

    {/* Add domain form */}
    {showForm && (
      <div className="panel mb-6 p-5 sm:p-6 border-primary/20">
        <div className="flex items-center gap-2 mb-4">
          <Globe2 size={18} className="text-primary" />
          <h2 className="text-base font-semibold">{t('admin.addDomainTitle')}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t('admin.addDomainDesc')}</p>
        <form onSubmit={create} className="grid gap-4 sm:grid-cols-[1fr_10rem_auto]">
          <label className="text-sm font-medium">
            {t('admin.domainName')}
            <input className="field mt-1.5" value={name} onChange={(e) => setName(e.target.value)} placeholder="example.com" required />
          </label>
          <label className="text-sm font-medium">
            DKIM Selector
            <input className="field mt-1.5" value={selector} onChange={(e) => setSelector(e.target.value)} placeholder="default" required />
          </label>
          <div className="flex items-end gap-2">
            <button disabled={creating} className="btn-primary h-10">
              {creating ? t('common.loading') : t('admin.addDomain')}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary h-10">
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    )}

    {error && <p className="mb-4 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}

    {items.length === 0 ? (
      <EmptyState title="admin.emptyDomains" />
    ) : (
      <div className="space-y-4">
        {items.map((item) => (
          <section key={item.id} className="panel overflow-hidden transition-shadow hover:shadow-md">
            <div className="flex flex-wrap items-center justify-between gap-3 p-5">
              <div className="flex items-center gap-3 min-w-0">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${allValid(item.dnsChecks) ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                  <Globe2 size={20} />
                </span>
                <div className="min-w-0">
                  <h2 className="font-semibold truncate">{item.name}</h2>
                  <p className="text-xs text-muted-foreground">
                    DKIM: <code className="bg-muted px-1 py-0.5 rounded">{item.dkimSelector}._domainkey</code>
                    {item.dnsChecks && (
                      <span className="ml-2">{validCount(item.dnsChecks)}/4 {t('admin.dnsOk')}</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                  className="btn-secondary flex items-center gap-1.5 h-9 px-3 text-sm">
                  <HelpCircle size={14} />{t('admin.dnsGuide')}
                </button>
                <button onClick={() => check(item)} className="btn-secondary flex items-center gap-1.5 h-9 px-3 text-sm">
                  <RefreshCw size={14} />{t('admin.dnsCheck')}
                </button>
                <button onClick={() => remove(item.id)} className="rounded-lg border p-2 text-destructive hover:bg-destructive/10 transition-colors" aria-label="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* DNS status pills */}
            {item.dnsChecks && (
              <div className="flex flex-wrap gap-2 px-5 pb-4">
                {item.dnsChecks.map((c) => (
                  <span key={c.recordType} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${c.valid ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                    {c.valid ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                    {c.recordType}
                  </span>
                ))}
              </div>
            )}

            {/* Expandable DNS guide */}
            {expanded === item.id && item.dnsChecks && (
              <div className="border-t px-5 pb-5 bg-muted/20">
                <DNSGuide domain={item} dnsChecks={item.dnsChecks} />
              </div>
            )}
          </section>
        ))}
      </div>
    )}
  </>
}
